/**
 * fas: solid icons
 * fass: solid icons (shaking)
 * far: regular icons
 * fasr: regular icons (shaking)
 * fal: light icons
 * fasl: light icons (shaking)
 * fat: thin icons
 * fast: thin icons (shaking)
 * fad: duotone icons
 * fasds: duotone icons (shaking)
 * fab: brand icons
 * fak: kawaii icons
 * fakd: kawaii icons (shaking)
 * @explain
 * when you use `fad` prefix, you must set `primaryColor` and `secondaryColor` props
 * @example
 * ```html
 * <fa-icon prefix="fad" iconName="address-book" primaryColor="#ff0000" secondaryColor="#00ff00"></fa-icon>
 * ```
 */
export type IconPrefix = 'fas' | 'fass' | 'far' | 'fasr' | 'fal' | 'fasl' | 'fat' | 'fast' | 'fad' | 'fasds' | 'fab' | 'fak' | 'fakd';
export type IconStyle = 'solid' | 'regular' | 'light' | 'thin' | 'duotone' | 'brands';

export type AnimationProp = 'beat' | 'fade' | 'beat-fade' | 'bounce' | 'flip' | 'shake' | 'spin' | 'spin-reverse' | 'spin-pulse' | 'spin-pulse-reverse';
export type FlipProp = 'x' | 'y' | 'xy';
export type SizeProp = '2xs' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '1x' | '2x' | '3x' | '4x' | '5x' | '6x' | '7x' | '8x' | '9x' | '10x';
export type PullProp = 'left' | 'right';
export type BorderProps = {
	[p in 'color' | 'padding' | 'radius' | 'style' | 'width' | 'margin']: p extends keyof CSSStyleDeclaration ? CSSStyleDeclaration[p] : never;
};
export type RotateProp = 90 | 180 | 270;

export type FaIconInit = 'stack' | 'icon';

export type FaIconBaseProps = {
	readonly iconName: string;
	readonly color?: string;
} & ({prefix: IconPrefix; faStyle?: never} | {prefix?: never; faStyle: `fa-${IconStyle}` | IconStyle});

export type FaDuotoneIconProps = FaIconBaseProps & {
	prefix: 'fad';
	primaryColor: string;
	secondaryColor: string;
};

export type FaIconAdditionalProps = {
	readonly animation: AnimationProp;
	readonly border: Partial<BorderProps>;
	readonly fixedWidth: boolean;
	readonly counter: number;
	readonly inverse: boolean;
	readonly flip: FlipProp;
	readonly size: SizeProp;
	readonly pull: PullProp;
	readonly rotate: RotateProp;
};

/* -------------------------------------------------------------------------------------------------------------- */
/**
 * to use it in our custom icon component:
 * <icon init="stack"
 * // the background icon will be sets in `backgroundIcon` prop
 * //	the foreground icon will be sets in `foregroundIcon` prop
 * [backgroundIcon]="{prefix: 'fas', iconName: 'square'}" // will get '2x' size automatically
 * [foregroundIcon]="{prefix: 'fas', iconName: 'terminal'}" // will get '1x' size automatically
 * ></icon>
 */
export type FaStackIconProps = {
	readonly backgroundIcon: FaIconProps & {readonly stackItemSize?: '2x'};
	readonly foregroundIcon: FaIconProps & {readonly stackItemSize?: '1x'};
};
/* -------------------------------------------------------------------------------------------------------------- */
export type FaIconProps<Base extends FaIconBaseProps | FaDuotoneIconProps = FaIconBaseProps | FaDuotoneIconProps> = Base & Partial<FaIconAdditionalProps>;

export function isFaDuotoneIconProps(props: FaIconProps): props is FaDuotoneIconProps {
	return props.prefix === 'fad' || props.faStyle === 'duotone';
}
